import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowLeft as ArrowBackIcon,
  Plus as AddIcon,
  Sparkles as GenerateIcon,
  ListPlus as GenerateAllIcon,
  Eye as ViewIcon,
  CheckCircle2 as ApprovedIcon,
  Hourglass as DraftIcon,
  AlertTriangle as MissingIcon,
  AlertCircle as FailedIcon,
  Calendar as EventIcon,
  Repeat1 as SingleIcon,
  Repeat as SeriesIcon,
  FilePlus as NoteAddIcon,
  Trash2 as DeleteIcon,
} from 'lucide-react';
import api from '../../utils/axios';
import {
  listAppointments,
  listNotes,
  createAppointment,
  submitNoteForAppointment,
  deleteNote,
  generateAllMissingNotes,
} from '../../utils/notesApi';

// Derive a display MRN — stored value when present, otherwise a deterministic
// synthetic "MRN-XXXXXX" from the patient id (matches PatientsManagement.js).
function deriveMrn(p) {
  if (p.mrn) return p.mrn;
  return 'MRN-' + (p.patient_id || p.id || '').replaceAll('-', '').slice(0, 6).toUpperCase();
}

// Surface the most useful message from a DRF error body: `detail`, then
// `non_field_errors`, then the first field-keyed error (e.g. {"date": [...]}).
function firstApiError(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.detail) return data.detail;
  if (Array.isArray(data.non_field_errors) && data.non_field_errors[0]) return data.non_field_errors[0];
  for (const v of Object.values(data)) {
    if (Array.isArray(v) && v.length && typeof v[0] === 'string') return v[0];
    if (typeof v === 'string') return v;
  }
  return null;
}

const STATUS_CONFIG = {
  missing_note: { label: 'Missing Note', color: 'warning', Icon: MissingIcon },
  note_draft: { label: 'Draft', color: 'info', Icon: DraftIcon },
  note_approved: { label: 'Approved', color: 'success', Icon: ApprovedIcon },
  scheduled: { label: 'Scheduled', color: 'default', Icon: null },
  completed: { label: 'Completed', color: 'default', Icon: null },
};

// Note-level statuses (separate from appointment statuses above)
const NOTE_STATUS_CONFIG = {
  generating: { label: 'Generating…', color: 'info' },
  draft: { label: 'Draft', color: 'info' },
  failed: { label: 'Failed', color: 'error' },
  approved: { label: 'Approved', color: 'success' },
};

const AppointmentRow = ({ appointment, note, onGenerate, generating, onAddManual, onDeleteNote, deletingNoteId }) => {
  const navigate = useNavigate();
  const cfg = STATUS_CONFIG[appointment.status] || { label: appointment.status, color: 'default' };
  const isMissing = ['completed', 'missing_note'].includes(appointment.status) && !note;
  const isDraft = note?.status === 'draft';
  const isGenerating = note?.status === 'generating';
  const isFailed = note?.status === 'failed';
  const isDeleting = deletingNoteId === note?.id;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 90 }}>
          {appointment.date}
        </Typography>
        {appointment.therapist && (
          <Typography variant="body2" color="text.secondary">
            {appointment.therapist}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label={cfg.label} color={cfg.color} size="small" />
        {(isGenerating || isFailed) && (
          <Chip
            label={NOTE_STATUS_CONFIG[note.status].label}
            color={NOTE_STATUS_CONFIG[note.status].color}
            size="small"
            icon={isGenerating ? <CircularProgress size={12} color="inherit" /> : undefined}
          />
        )}

        {isMissing && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={generating ? <CircularProgress size={14} /> : <GenerateIcon size={16} />}
              onClick={() => onGenerate(appointment.id)}
              disabled={generating}
            >
              Generate
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<NoteAddIcon size={16} />}
              onClick={() => onAddManual(appointment)}
              disabled={generating}
            >
              Add Note
            </Button>
          </>
        )}

        {isFailed && (
          <Tooltip title="Generation failed — click to retry">
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<FailedIcon size={16} />}
              onClick={() => navigate(`/notes/review/${note.id}`)}
            >
              Retry
            </Button>
          </Tooltip>
        )}

        {isDraft && (
          <Button
            size="small"
            variant="contained"
            startIcon={<ViewIcon size={16} />}
            onClick={() => navigate(`/notes/review/${note.id}`)}
          >
            Review Draft
          </Button>
        )}

        {note?.status === 'approved' && (
          <Button
            size="small"
            variant="text"
            startIcon={<ViewIcon size={16} />}
            onClick={() => navigate(`/notes/review/${note.id}`)}
          >
            View
          </Button>
        )}

        {note && (
          <Tooltip title="Delete note">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDeleteNote(note.id, appointment.id)}
                disabled={isDeleting}
              >
                {isDeleting ? <CircularProgress size={16} color="error" /> : <DeleteIcon size={16} />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// Frequency options: label, interval in days
const FREQUENCY_OPTIONS = [
  { label: '3x / week (M/W/F)', days: [0, 2, 4] },  // offsets within a week
  { label: '2x / week (T/Th)', days: [1, 3] },
  { label: 'Weekly', days: [0] },
  { label: 'Biweekly', days: [0], step: 14 },
];

/** Format a Date as 'YYYY-MM-DD' using local calendar (avoids UTC shift). */
function toLocalDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Add n days to a 'YYYY-MM-DD' string, returns new string. Uses noon to avoid DST edge cases. */
function addDaysStr(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return toLocalDateStr(d);
}

/**
 * Generate a list of ISO date strings for a series.
 * startDate is always included as the first date.
 * Subsequent dates follow the frequency pattern up to and including endDate.
 */
function buildSeriesDates(startDate, frequencyIndex, endDate) {
  if (!startDate || !endDate || endDate < startDate) return startDate ? [startDate] : [];
  const freq = FREQUENCY_OPTIONS[frequencyIndex];
  const dates = [startDate]; // start date is always first
  const MAX_DATES = 120;

  if (freq.step) {
    let cur = addDaysStr(startDate, freq.step);
    while (dates.length < MAX_DATES && cur <= endDate) {
      dates.push(cur);
      cur = addDaysStr(cur, freq.step);
    }
  } else {
    // Week-based: find all pattern-day occurrences strictly after startDate and <= endDate
    const start = new Date(startDate + 'T12:00:00');
    const dow = start.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const weekStart = new Date(start.getTime() + mondayOffset * 86400000);

    outer: for (let week = 0; week < 104; week++) {
      for (const offset of freq.days) {
        const d = new Date(weekStart.getTime() + (week * 7 + offset) * 86400000);
        const dStr = toLocalDateStr(d);
        if (dStr <= startDate) continue;  // start already added
        if (dStr > endDate) break outer;
        dates.push(dStr);
        if (dates.length >= MAX_DATES) break outer;
      }
    }
  }
  return dates;
}

const ManualNoteDialog = ({ open, appointment, onClose, onCreated }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setText('');
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await submitNoteForAppointment(appointment.id, { text });
      onCreated(res.data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save note.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NoteAddIcon size={16} />
        Add Note — {appointment?.date}
        {appointment?.therapist && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({appointment.therapist})
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label="Note Text"
          multiline
          rows={12}
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
          placeholder="Enter the clinical note for this appointment…"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!text.trim() || saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Save Draft'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddAppointmentDialog = ({ open, patientId, onClose, onCreated }) => {
  const [mode, setMode] = useState('single');         // 'single' | 'series' | 'adhoc'
  const [date, setDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [therapist, setTherapist] = useState('');
  const [frequencyIndex, setFrequencyIndex] = useState(0);
  const [adhocDates, setAdhocDates] = useState([]);   // for adhoc mode
  const [adhocInput, setAdhocInput] = useState('');   // date picker value for adhoc
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);     // '3 / 8' during series save
  const [error, setError] = useState(null);

  const previewDates = mode === 'series' && date && endDate
    ? buildSeriesDates(date, frequencyIndex, endDate)
    : [];

  const handleClose = () => {
    setMode('single');
    setDate('');
    setEndDate('');
    setTherapist('');
    setFrequencyIndex(0);
    setAdhocDates([]);
    setAdhocInput('');
    setError(null);
    setProgress(null);
    onClose();
  };

  const handleAddAdhocDate = () => {
    if (!adhocInput || adhocDates.includes(adhocInput)) return;
    setAdhocDates((prev) => [...prev, adhocInput].sort());
    setAdhocInput('');
  };

  const handleRemoveAdhocDate = (d) => {
    setAdhocDates((prev) => prev.filter((x) => x !== d));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setProgress(null);
    try {
      if (mode === 'single') {
        const res = await createAppointment(patientId, { date, therapist });
        onCreated([res.data]);
      } else if (mode === 'adhoc') {
        const created = [];
        for (let i = 0; i < adhocDates.length; i++) {
          setProgress(`${i + 1} / ${adhocDates.length}`);
          const res = await createAppointment(patientId, { date: adhocDates[i], therapist });
          created.push(res.data);
        }
        onCreated(created);
      } else {
        const dates = buildSeriesDates(date, frequencyIndex, endDate);
        const created = [];
        for (let i = 0; i < dates.length; i++) {
          setProgress(`${i + 1} / ${dates.length}`);
          const res = await createAppointment(patientId, { date: dates[i], therapist });
          created.push(res.data);
        }
        onCreated(created);
      }
      handleClose();
    } catch (err) {
      setError(firstApiError(err.response?.data) || 'Failed to create appointment(s).');
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  const canSave =
    (mode === 'single' && date) ||
    (mode === 'series' && date && endDate && endDate >= date && previewDates.length > 0) ||
    (mode === 'adhoc' && adhocDates.length > 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EventIcon size={16} />
        Add Appointments
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Mode toggle */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, v) => v && setMode(v)}
          size="small"
          sx={{ mb: 3 }}
        >
          <ToggleButton value="single">
            <SingleIcon size={16} style={{marginRight: 4}} />
            Single Visit
          </ToggleButton>
          <ToggleButton value="series">
            <SeriesIcon size={16} style={{marginRight: 4}} />
            Note Series
          </ToggleButton>
          <ToggleButton value="adhoc">
            <AddIcon size={16} style={{marginRight: 4}} />
            Ad Hoc
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Single / Series: start date */}
        {mode !== 'adhoc' && (
          <TextField
            label={mode === 'single' ? 'Service Date' : 'Series Start Date'}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
        )}

        {mode === 'series' && (
          <>
            <TextField
              label="Series End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: date || undefined }}
              sx={{ mb: 2 }}
            />

            <TextField
              select
              label="Frequency"
              value={frequencyIndex}
              onChange={(e) => setFrequencyIndex(Number(e.target.value))}
              fullWidth
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            >
              {FREQUENCY_OPTIONS.map((opt, i) => (
                <option key={i} value={i}>{opt.label}</option>
              ))}
            </TextField>

            {previewDates.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Preview — {previewDates.length} visit{previewDates.length !== 1 ? 's' : ''}
                </Typography>
                <Box
                  sx={{
                    maxHeight: 130,
                    overflowY: 'auto',
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  {previewDates.map((d) => (
                    <Chip key={d} label={d} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}

        {mode === 'adhoc' && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Pick a Date"
                type="date"
                value={adhocInput}
                onChange={(e) => setAdhocInput(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                onClick={handleAddAdhocDate}
                disabled={!adhocInput || adhocDates.includes(adhocInput)}
                sx={{ mt: 0.5, flexShrink: 0 }}
              >
                Add
              </Button>
            </Box>

            {adhocDates.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {adhocDates.length} date{adhocDates.length !== 1 ? 's' : ''} selected
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {adhocDates.map((d) => (
                    <Chip
                      key={d}
                      label={d}
                      size="small"
                      onDelete={() => handleRemoveAdhocDate(d)}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}

        <TextField
          label="Therapist (optional)"
          value={therapist}
          onChange={(e) => setTherapist(e.target.value)}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          {saving
            ? progress
              ? <>Creating… {progress} <CircularProgress size={14} sx={{ ml: 1 }} /></>
              : <CircularProgress size={20} />
            : mode === 'series'
              ? `Create ${previewDates.length} Appointment${previewDates.length !== 1 ? 's' : ''}`
              : mode === 'adhoc'
              ? `Create ${adhocDates.length} Appointment${adhocDates.length !== 1 ? 's' : ''}`
              : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PatientNotesPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  // Auto-open the add dialog when navigated here with ?startSeries=true
  const [addDialogOpen, setAddDialogOpen] = useState(() => searchParams.get('startSeries') === 'true');
  const [manualNoteAppointment, setManualNoteAppointment] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [patientRes, apptRes, notesRes] = await Promise.all([
        api.get(`/patients/${patientId}/`),
        listAppointments(patientId),
        listNotes(patientId),
      ]);
      setPatient(patientRes.data);
      const apptRaw = apptRes.data;
      setAppointments(Array.isArray(apptRaw) ? apptRaw : (apptRaw?.results ?? []));
      const notesRaw = notesRes.data;
      setNotes(Array.isArray(notesRaw) ? notesRaw : (notesRaw?.results ?? []));
    } catch (err) {
      setError('Failed to load patient data.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (location.state?.refreshAt) {
      load();
    }
  }, [location.state, load]);

  // Map appointment id → note
  const noteByAppointment = {};
  notes.forEach((n) => {
    if (n.appointment) noteByAppointment[n.appointment] = n;
  });

  const missingAppointments = appointments.filter(
    (a) => ['completed', 'missing_note'].includes(a.status) && !noteByAppointment[a.id]
  );
  const draftNotes = notes.filter((n) => n.status === 'draft' || n.status === 'generating');

  const handleGenerate = async (appointmentId) => {
    setGeneratingId(appointmentId);
    setError(null);
    try {
      const res = await submitNoteForAppointment(appointmentId);
      setNotes((prev) => [...prev, res.data]);
      setAppointments((prev) =>
        prev.map((a) => a.id === appointmentId ? { ...a, status: 'note_draft' } : a)
      );
      navigate(`/notes/review/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate note.');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleGenerateAll = async () => {
    setGeneratingAll(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await generateAllMissingNotes(patientId);
      const count = res.data.generated_count;
      setSuccessMsg(`${count} note${count !== 1 ? 's' : ''} queued for generation. They will appear as drafts once ready.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate notes.');
    } finally {
      setGeneratingAll(false);
    }
  };

  const handleAppointmentCreated = (newAppts) => {
    // newAppts is always an array (single or series)
    setAppointments((prev) => [...prev, ...newAppts].sort((a, b) => a.date.localeCompare(b.date)));
  };

  const handleManualNoteCreated = (note) => {
    navigate(`/notes/review/${note.id}`);
  };

  const handleDeleteNote = async (noteId, appointmentId) => {
    setDeletingNoteId(noteId);
    setError(null);
    try {
      await deleteNote(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      setAppointments((prev) =>
        prev.map((a) => a.id === appointmentId ? { ...a, status: 'completed' } : a)
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete note.');
    } finally {
      setDeletingNoteId(null);
    }
  };

  if (loading && !patient) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton component={Link} to="/notes" size="small">
          <ArrowBackIcon size={16} />
        </IconButton>
        <Typography variant="h4">
          {patient ? `${patient.first_name} ${patient.last_name}` : 'Patient Notes'}
        </Typography>
      </Box>
      {patient && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 5 }}>
          DOB: {patient.date_of_birth} · MRN: {deriveMrn(patient)}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {/* Summary + actions */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {missingAppointments.length > 0 && (
          <Chip label={`${missingAppointments.length} missing notes`} color="warning" />
        )}
        {draftNotes.length > 0 && (
          <Chip label={`${draftNotes.length} drafts`} color="info" />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {missingAppointments.length > 0 && (
          <Button
            variant="contained"
            startIcon={generatingAll ? <CircularProgress size={16} /> : <GenerateAllIcon size={16} />}
            onClick={handleGenerateAll}
            disabled={generatingAll}
          >
            Generate All Missing Notes
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<AddIcon size={16} />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Appointment
        </Button>
      </Box>

      {/* Appointment timeline */}
      <Paper elevation={1}>
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Appointment Timeline
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : appointments.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No appointments yet. Add an appointment to get started.
            </Typography>
          </Box>
        ) : (
          appointments.map((appt) => (
            <AppointmentRow
              key={appt.id}
              appointment={appt}
              note={noteByAppointment[appt.id]}
              onGenerate={handleGenerate}
              generating={generatingId === appt.id}
              onAddManual={setManualNoteAppointment}
              onDeleteNote={handleDeleteNote}
              deletingNoteId={deletingNoteId}
            />
          ))
        )}
      </Paper>

      <AddAppointmentDialog
        open={addDialogOpen}
        patientId={patientId}
        onClose={() => setAddDialogOpen(false)}
        onCreated={handleAppointmentCreated}
      />

      <ManualNoteDialog
        open={!!manualNoteAppointment}
        appointment={manualNoteAppointment}
        onClose={() => setManualNoteAppointment(null)}
        onCreated={handleManualNoteCreated}
      />
    </Container>
  );
};

export default PatientNotesPage;
